// Serenity Star API Client for DFS Collections
import { config } from '../config/environment';

const API_BASE = `${config.serenityBaseUrl}/api/v2`;

interface QueryResult {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  executionTimeMs: number;
}

// Response from creating/getting a conversation
interface ConversationResponse {
  chatId: string;
  content: string;
  conversationStarters: string[];
  version: number;
  useVision: boolean;
}

// Response from executing an agent (both activity and chat messages)
interface ExecuteResponse {
  content: string;
  jsonContent?: unknown;
  instanceId: string;
  userMessageId?: string;
  agentMessageId?: string;
  completionUsage?: {
    completionTokens: number;
    promptTokens: number;
    totalTokens: number;
  };
}

// Legacy interface for backward compatibility
interface AgentResponse {
  response: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
}

class SerenityClient {
  private apiKey: string;
  private datasetId: string;

  constructor() {
    this.apiKey = config.serenityApiKey;
    this.datasetId = config.datasetId;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': this.apiKey,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  // Dataset queries
  async query(sql: string): Promise<QueryResult> {
    return this.fetch<QueryResult>(`/Dataset/${this.datasetId}/query`, {
      method: 'POST',
      body: JSON.stringify({
        sql,
        format: 'application/json',
      }),
    });
  }

  // Execute SQL and return data as array of objects
  async queryData<T>(sql: string): Promise<T[]> {
    const result = await this.query(sql);
    if (!result.rows || result.rows.length === 0) return [];
    
    // Convert rows array to objects using columns
    return result.rows.map(row => {
      const obj: Record<string, unknown> = {};
      result.columns.forEach((col, idx) => {
        obj[col] = row[idx];
      });
      return obj as T;
    });
  }

  // Append data to a table
  async appendToTable(tableIdentifier: string, data: Record<string, unknown>[]): Promise<void> {
    // Convert data to CSV format
    if (data.length === 0) return;
    
    const columns = Object.keys(data[0]);
    const csvRows = [
      columns.join(','),
      ...data.map(row => columns.map(col => {
        const val = row[col];
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return String(val);
      }).join(','))
    ];
    const csvContent = csvRows.join('\n');
    
    const formData = new FormData();
    formData.append('file', new Blob([csvContent], { type: 'text/csv' }), 'data.csv');

    const response = await fetch(
      `${API_BASE}/Dataset/${this.datasetId}/table/${tableIdentifier}/append`,
      {
        method: 'POST',
        headers: {
          'X-API-KEY': this.apiKey,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to append data: ${error}`);
    }
  }

  // Create or get a conversation with an assistant agent
  // Returns chatId needed for subsequent messages
  // inputParameters are passed to the agent as context variables
  async createConversation(
    agentCode: string,
    userIdentifier: string,
    inputParameters?: Array<{ key: string; value: string }>
  ): Promise<ConversationResponse> {
    const body: Record<string, unknown> = { userIdentifier };
    
    if (inputParameters && inputParameters.length > 0) {
      body.inputParameters = inputParameters;
    }
    
    return this.fetch<ConversationResponse>(`/Agent/${agentCode}/conversation`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Send a message to an existing conversation
  // Can include additional context variables
  async sendMessage(
    agentCode: string,
    chatId: string,
    message: string,
    contextVariables?: Record<string, string>
  ): Promise<ExecuteResponse> {
    const params: Array<{ Key: string; Value: string }> = [
      { Key: 'chatId', Value: chatId },
      { Key: 'message', Value: message },
    ];
    
    // Add any additional context variables
    if (contextVariables) {
      Object.entries(contextVariables).forEach(([key, value]) => {
        params.push({ Key: key, Value: value });
      });
    }
    
    return this.fetch<ExecuteResponse>(`/agent/${agentCode}/execute`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Chat with an assistant agent (convenience method)
  // Creates conversation if no chatId provided, then sends message
  // contextParams are passed to the agent for dataset queries (e.g., debtorId, phone)
  async chatWithAgent(
    agentCode: string,
    message: string,
    chatId?: string,
    userIdentifier?: string,
    contextParams?: Record<string, string>
  ): Promise<AgentResponse> {
    // If no chatId, create a new conversation first with context parameters
    let effectiveChatId = chatId;
    if (!effectiveChatId) {
      // Convert context params to inputParameters format
      const inputParameters = contextParams 
        ? Object.entries(contextParams).map(([key, value]) => ({ key, value }))
        : undefined;
      
      const conv = await this.createConversation(
        agentCode, 
        userIdentifier || 'anonymous',
        inputParameters
      );
      effectiveChatId = conv.chatId;
    }

    // Send the message with context variables
    const result = await this.sendMessage(agentCode, effectiveChatId, message, contextParams);
    
    return {
      response: result.content,
      sessionId: effectiveChatId,
      metadata: {
        instanceId: result.instanceId,
        userMessageId: result.userMessageId,
        agentMessageId: result.agentMessageId,
      },
    };
  }

  // Execute an activity agent
  async executeAgent(
    agentCode: string,
    variables: Record<string, string>
  ): Promise<AgentResponse> {
    const params = Object.entries(variables).map(([key, value]) => ({
      Key: key,
      Value: value,
    }));
    const result = await this.fetch<ExecuteResponse>(`/agent/${agentCode}/execute`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    
    return {
      response: result.content,
      metadata: {
        jsonContent: result.jsonContent,
        instanceId: result.instanceId,
      },
    };
  }

  // Upload a file to Volatile Knowledge
  async uploadVolatileKnowledge(file: File): Promise<{ id: string; publicUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/VolatileKnowledge`, {
      method: 'POST',
      headers: {
        'X-API-KEY': this.apiKey,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload file: ${error}`);
    }

    return response.json();
  }

  // Chat method alias for compatibility
  // contextParams: { debtorId, phone, etc. } passed to agent for automatic identification
  async chat(
    agentCode: string,
    message: string,
    chatId?: string,
    userIdentifier?: string,
    contextParams?: Record<string, string>
  ): Promise<AgentResponse> {
    return this.chatWithAgent(agentCode, message, chatId, userIdentifier, contextParams);
  }

  // Insert data into a table (simplified for demo)
  async insertData(tableName: string, data: Record<string, unknown>): Promise<void> {
    await this.appendToTable(tableName, [data]);
  }

  // Classify a document using the analyzer agent
  async classifyDocument(
    debtorId: string,
    documentId: string,
    documentType: string,
    volatileKnowledgeId?: string
  ): Promise<{ documentType: string; confidence: number; extractedData?: unknown }> {
    const variables: Record<string, string> = {
      debtorId,
      documentId,
      documentType,
    };
    
    if (volatileKnowledgeId) {
      variables.volatileKnowledgeIds = JSON.stringify([volatileKnowledgeId]);
    }
    
    const result = await this.executeAgent('dfs-analizador', variables);
    
    // Try to parse JSON from response
    try {
      const parsed = JSON.parse(result.response);
      return {
        documentType: parsed.document_type_confirmed || documentType,
        confidence: parsed.authenticity_score || 0.8,
        extractedData: parsed.extracted_data,
      };
    } catch {
      return {
        documentType,
        confidence: 0.7,
      };
    }
  }

  // Evaluate a hardship request using the evaluator agent
  async evaluateHardship(
    requestId: string,
    debtorId: string,
    caseId: string
  ): Promise<{
    verified: boolean;
    category: string;
    severityScore: number;
    recommendedRelief: string;
    reliefTerms?: string;
    approvalRecommendation: string;
    confidenceScore: number;
  }> {
    const result = await this.executeAgent('dfs-evaluador', {
      requestId,
      debtorId,
      caseId,
    });
    
    // Try to parse JSON from response
    try {
      const parsed = JSON.parse(result.response);
      return {
        verified: parsed.hardship_verified ?? true,
        category: parsed.category || 'other',
        severityScore: parsed.severity_score || 50,
        recommendedRelief: parsed.recommended_relief || 'payment_pause',
        reliefTerms: parsed.relief_terms,
        approvalRecommendation: parsed.approval_recommendation || 'pending_review',
        confidenceScore: parsed.confidence_score || 0.7,
      };
    } catch {
      return {
        verified: true,
        category: 'other',
        severityScore: 50,
        recommendedRelief: 'payment_pause',
        approvalRecommendation: 'pending_review',
        confidenceScore: 0.7,
      };
    }
  }
}

export const serenityClient = new SerenityClient();
